// =============================================================================
// VandieZ admin-web -- Support ticket queue (/tickets)
// =============================================================================
// Lists tickets. Category chips pre-filter the queue (KYC / payment /
// trip / account / other / all). "Mine" toggle scopes to tickets
// assigned to the calling admin. Click a row → detail page.
// =============================================================================

import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  LifeBuoy,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { supportAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  TicketCategory,
  TicketRead,
  TicketStatus,
} from "@/types/support";

const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 50;

const CATEGORY_FILTERS: { label: string; value: TicketCategory | "all" }[] = [
  { label: "All", value: "all" },
  { label: "KYC", value: "kyc" },
  { label: "Payment", value: "payment" },
  { label: "Trip", value: "trip" },
  { label: "Account", value: "account" },
  { label: "Other", value: "other" },
];

const STATUS_FILTERS: { label: string; value: TicketStatus | "all" }[] = [
  { label: "Any status", value: "all" },
  { label: "Open", value: "open" },
  { label: "In progress", value: "in_progress" },
  { label: "Resolved", value: "resolved" },
  { label: "Closed", value: "closed" },
];

const STATUS_PILL: Record<TicketStatus, string> = {
  open: "bg-warning-subtle text-warning-subtle-foreground",
  in_progress: "bg-primary-subtle text-primary-subtle-foreground",
  resolved: "bg-success-subtle text-success-subtle-foreground",
  closed: "bg-muted text-muted-foreground",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function TicketsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState<TicketCategory | "all">("all");
  const [statusFilter, setStatusFilter] = useState<TicketStatus | "all">("all");
  const [mineOnly, setMineOnly] = useState<boolean>(false);
  const [items, setItems] = useState<TicketRead[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<boolean>(false);

  const fetchPage = useCallback(
    async (nextOffset: number, signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await supportAdminApi.queue(
          accessToken,
          {
            limit: pageSize,
            offset: nextOffset,
            ...(category !== "all" ? { category } : {}),
            ...(statusFilter !== "all" ? { status: statusFilter } : {}),
            mine: mineOnly,
          },
          signal,
        );
        setItems(res.items);
        setTotal(res.total);
        setOffset(res.offset);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        const msg =
          e instanceof ApiError ? e.problem.detail : "Unexpected error.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, category, statusFilter, mineOnly, pageSize],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchPage(0, controller.signal);
    return () => {
      controller.abort();
    };
  }, [fetchPage]);

  const exportCsv = async (): Promise<void> => {
    if (accessToken === null) {return;}
    setExporting(true);
    try {
      const res = await supportAdminApi.queue(accessToken, {
        limit: 200,
        offset: 0,
        ...(category !== "all" ? { category } : {}),
        ...(statusFilter !== "all" ? { status: statusFilter } : {}),
        mine: mineOnly,
      });
      const header = [
        "last_activity_at",
        "subject",
        "category",
        "message_count",
        "status",
        "priority",
        "created_at",
      ];
      const escape = (v: string): string =>
        /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
      const csv = [
        header.join(","),
        ...res.items.map((r) =>
          [
            r.last_activity_at,
            r.subject,
            r.category,
            r.message_count,
            r.status,
            r.priority,
            r.created_at,
          ]
            .map((c) => escape(String(c)))
            .join(","),
        ),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vandiez-tickets-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const canPrev = offset > 0;
  const canNext = offset + items.length < total;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Support tickets"
        description="Pre-routed by category. Tickets sort by most recent activity."
        actions={
          <Button
            variant="outline"
            onClick={() => void exportCsv()}
            disabled={exporting || items.length === 0}
          >
            <Download className="mr-1 h-4 w-4" />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        }
      />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LifeBuoy className="h-5 w-5" /> Queue
            </CardTitle>
            <CardDescription>
              Pre-routed by category. Tickets sort by most recent activity.
              {total > 0 && (
                <span className="ml-1 text-foreground">
                  Showing {offset + 1}–{offset + items.length} of {total}.
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-3 flex flex-wrap gap-1">
              {CATEGORY_FILTERS.map((c) => (
                <button
                  key={c.value}
                  onClick={() => {
                    setCategory(c.value);
                  }}
                  className={cn(
                    "rounded px-3 py-1 text-sm",
                    category === c.value
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="mb-4 flex flex-wrap items-center gap-1">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s.value}
                  onClick={() => {
                    setStatusFilter(s.value);
                  }}
                  className={cn(
                    "rounded px-2 py-0.5 text-xs",
                    statusFilter === s.value
                      ? "bg-secondary text-secondary-foreground"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {s.label}
                </button>
              ))}
              <label className="ml-auto flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={mineOnly}
                  onChange={(e) => {
                    setMineOnly(e.target.checked);
                  }}
                />
                Mine only
              </label>
            </div>

            {error !== null && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {loading && items.length === 0 ? (
              <TableSkeleton columns={6} />
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground">
                Nothing in this view right now.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-4">Activity</th>
                      <th className="py-2 pr-4">Subject</th>
                      <th className="py-2 pr-4">Category</th>
                      <th className="py-2 pr-4">Msgs</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => (
                      <tr
                        key={row.id}
                        className="cursor-pointer border-b last:border-b-0 hover:bg-muted/40"
                        onClick={() => {
                          navigate(`/tickets/${row.id}`);
                        }}
                      >
                        <td className="py-3 pr-4 text-xs text-muted-foreground">
                          {formatDate(row.last_activity_at)}
                        </td>
                        <td className="py-3 pr-4 font-medium">{row.subject}</td>
                        <td className="py-3 pr-4 capitalize">{row.category}</td>
                        <td className="py-3 pr-4">{row.message_count}</td>
                        <td className="py-3 pr-4">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                              STATUS_PILL[row.status],
                            )}
                          >
                            {row.status.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          <Button variant="link" size="sm" className="px-0">
                            Open →
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                disabled={!canPrev || loading}
                onClick={() => void fetchPage(Math.max(0, offset - pageSize))}
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Previous
              </Button>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>
                  {loading ? "Loading…" : `Page ${Math.floor(offset / pageSize) + 1}`}
                </span>
                <label className="flex items-center gap-1">
                  Rows:
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number.parseInt(e.target.value, 10));
                    }}
                    className="rounded border bg-background px-1 py-0.5 text-xs"
                  >
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </label>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={!canNext || loading}
                onClick={() => void fetchPage(offset + pageSize)}
              >
                Next <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
    </div>
  );
}
